import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, Search } from "lucide-react";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { MobileAvatar } from "@/components/mobile/mobile-avatar";
import { MobileScreenHeader } from "@/components/mobile/mobile-screen-header";
import { AgentStatusPill } from "@/components/mobile/agent-status-pill";
import { cn } from "@/lib/utils";

type StatusFilter = "all" | "active" | "paused" | "idle";

const FILTERS: { key: StatusFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "paused", label: "Paused" },
  { key: "idle", label: "Idle" },
];

function matchesStatus(agentStatus: string, filter: StatusFilter): boolean {
  if (filter === "all") return true;
  if (filter === "active") return agentStatus === "active" || agentStatus === "running";
  if (filter === "paused") return agentStatus === "paused";
  if (filter === "idle") return agentStatus === "idle";
  return true;
}

function relativeTime(iso: string | null): string {
  if (!iso) return "";
  const diffMs = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export function MobileAgentsList() {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<StatusFilter>("all");

  const counts = useMemo(() => {
    const c: Record<StatusFilter, number> = { all: 0, active: 0, paused: 0, idle: 0 };
    agents.forEach((a) => {
      c.all += 1;
      if (a.status === "active" || a.status === "running") c.active += 1;
      else if (a.status === "paused") c.paused += 1;
      else if (a.status === "idle") c.idle += 1;
    });
    return c;
  }, [agents]);

  const visible = useMemo(
    () =>
      agents.filter((a) => {
        if (!matchesStatus(a.status, filter)) return false;
        if (search.trim()) {
          const q = search.toLowerCase();
          return (
            a.name.toLowerCase().includes(q) ||
            (a.title || "").toLowerCase().includes(q) ||
            (a.department || "").toLowerCase().includes(q)
          );
        }
        return true;
      }),
    [agents, filter, search],
  );

  const byDept = useMemo(() => {
    const map = new Map<string, typeof agents>();
    visible.forEach((a) => {
      const key = a.department || "Unassigned";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(a);
    });
    return Array.from(map.entries());
  }, [visible]);

  return (
    <div className="flex h-full flex-col">
      <MobileScreenHeader
        title="Agents"
        subtitle={`${counts.all} deployed · ${counts.active} active now`}
        tall
      />

      {/* Search + filter pills */}
      <div className="px-4 pb-2 pt-3">
        <div className="glass-field flex h-[38px] items-center gap-2 rounded-[13px] px-3">
          <Search size={16} className="text-text-muted" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search agents, capabilities…"
            className="flex-1 bg-transparent text-[14px] text-text-primary outline-none placeholder:text-text-muted"
          />
        </div>
        <div className="mt-2.5 flex gap-1.5 overflow-x-auto">
          {FILTERS.map((f) => {
            const isActive = f.key === filter;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={cn(
                  "inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full px-3 text-[12px] font-medium",
                  isActive ? "mg-accent-fill font-semibold" : "glass-field text-text-secondary",
                )}
              >
                {f.label}
                <span className={cn("font-semibold", isActive ? "opacity-90" : "opacity-60")}>
                  {counts[f.key]}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div
        className="min-h-0 flex-1 overflow-auto px-4 pt-2"
        style={{ paddingBottom: "calc(96px + env(safe-area-inset-bottom, 0px))" }}
      >
        {byDept.length === 0 && (
          <div className="mt-8 text-center text-[13px] text-text-muted">No agents match.</div>
        )}
        {byDept.map(([dept, list]) => (
          <div key={dept} className="mb-[18px]">
            <div className="px-1 py-1.5 text-[11px] font-bold uppercase tracking-wider text-text-muted">
              {dept}
            </div>
            <div className="glass-card overflow-hidden rounded-[28px]">
              {list.map((a, i) => (
                <div
                  key={a.id}
                  className={cn(
                    "flex items-center gap-3 px-3.5 py-3",
                    i > 0 && "border-t border-border",
                  )}
                >
                  <MobileAvatar name={a.name} avatarUrl={a.avatarUrl} size={40} status={a.status} />
                  <button
                    onClick={() => navigate(`/tenants/${tenantId}/agents/${a.id}`)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <div className="text-[15px] font-semibold tracking-tight text-text-primary">
                      {a.name}
                    </div>
                    {a.title && (
                      <div className="mt-0.5 text-[12px] text-text-secondary">{a.title}</div>
                    )}
                    <div className="mt-1 flex items-center">
                      <AgentStatusPill status={a.status} mini />
                      {a.lastHeartbeatAt && (
                        <span className="ml-2 text-[11px] text-text-muted">
                          · {relativeTime(a.lastHeartbeatAt)}
                        </span>
                      )}
                    </div>
                  </button>
                  <button
                    onClick={() => navigate(`/tenants/${tenantId}/workspace?agentId=${a.id}`)}
                    className="mg-icon-btn text-accent"
                    aria-label={`Chat with ${a.name}`}
                  >
                    <MessageSquare size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
